import React, { useState, useEffect, useCallback } from 'react';
import { getAnalyticsSummary, getAppreciationTimeline, exportAnalyticsData, getAtRiskUsers, getDepartmentComparison } from '../services/analyticsService';
import { getCompanyValuesStats } from '../services/companyValueService';

const Analytics = () => {
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [companyValuesStats, setCompanyValuesStats] = useState([]);
  const [atRiskUsers, setAtRiskUsers] = useState([]);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [selectedInterval, setSelectedInterval] = useState('1m');
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const [summaryData, timelineData, valuesData, atRiskData, deptData] = await Promise.all([
        getAnalyticsSummary(selectedInterval),
        getAppreciationTimeline(selectedInterval),
        getCompanyValuesStats(),
        getAtRiskUsers(30),
        getDepartmentComparison(selectedInterval)
      ]);

      setSummary(summaryData);
      setTimeline(timelineData);
      setCompanyValuesStats(valuesData);
      setAtRiskUsers(atRiskData.users || []);
      setDepartmentStats(deptData.departments || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedInterval]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleExport = async () => {
    try {
      const blob = await exportAnalyticsData(selectedInterval);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `apprecio_analytics_${selectedInterval}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  const getTeamPulseScore = () => {
    if (!summary) return 0;
    return summary.participation_rate;
  };

  const getTeamPulseColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const intervals = [
    { value: '1w', label: '1 Week' },
    { value: '1m', label: '1 Month' },
    { value: '3m', label: '3 Months' },
    { value: '6m', label: '6 Months' },
    { value: '1y', label: '1 Year' },
    { value: 'all', label: 'All Time' }
  ];

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        <div className="text-center py-10">Loading analytics...</div>
      </div>
    );
  }

  const pulseScore = getTeamPulseScore();

  return (
    <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold gradient-text">Culture Analytics</h1>
          <p className="mt-2 text-base text-gray-600">
            Actionable insights to drive cultural transformation.
          </p>
        </div>
        <button
          onClick={handleExport}
          className="btn-gradient text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm hover:shadow-md transition-all"
        >
          <i className="fas fa-download mr-2"></i>
          Export Data
        </button>
      </div>

      {/* Time interval tabs */}
      <div className="mb-6 border-b border-gray-200 overflow-x-auto">
        <nav className="flex space-x-8 min-w-max">
          {intervals.map(interval => (
            <button
              key={interval.value}
              onClick={() => setSelectedInterval(interval.value)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                selectedInterval === interval.value
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {interval.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Metrics Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Appreciations */}
        <div className="bg-white rounded-xl shadow-md p-6 card-hover border-l-4 border-blue-500 fade-in">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Total Appreciations</h3>
          <p className="text-4xl font-bold gradient-text">{summary?.total_appreciations || 0}</p>
        </div>

        {/* Active Users */}
        <div className="bg-white rounded-xl shadow-md p-6 card-hover border-l-4 border-green-500 fade-in">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Active Users</h3>
          <p className="text-4xl font-bold gradient-text">{summary?.active_users || 0}</p>
          <p className="text-xs text-gray-500 mt-1">of {summary?.total_users || 0} total</p>
        </div>

        {/* Participation Rate */}
        <div className="bg-white rounded-xl shadow-md p-6 card-hover border-l-4 border-purple-500 fade-in">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Participation Rate</h3>
          <p className="text-4xl font-bold gradient-text">{summary?.participation_rate || 0}%</p>
        </div>

        {/* Team Pulse */}
        <div className="bg-white rounded-xl shadow-md p-6 card-hover border-l-4 border-indigo-500 fade-in">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Team Pulse</h3>
          <p className={`text-4xl font-bold ${getTeamPulseColor(pulseScore)}`}>
            {pulseScore.toFixed(0)}%
          </p>
          <p className="text-xs text-gray-500 mt-1">Health Score</p>
        </div>
      </div>

      {/* Engagement Timeline */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Engagement Timeline</h2>
        {timeline.length > 0 ? (
          <div className="overflow-x-auto">
            <div className="flex items-end space-x-1 min-w-full h-64 pb-8">
              {timeline.map((point, index) => {
                const maxCount = Math.max(...timeline.map(p => p.count));
                const heightPercent = maxCount > 0 ? (point.count / maxCount) * 100 : 0;

                return (
                  <div key={index} className="flex flex-col items-center justify-end flex-1 h-full group">
                    <div className="relative w-full flex flex-col items-center justify-end h-full">
                      {/* Bar */}
                      <div
                        className="bg-blue-500 hover:bg-blue-600 w-full rounded-t transition-colors cursor-pointer"
                        style={{ height: `${heightPercent}%`, minHeight: point.count > 0 ? '4px' : '0px' }}
                        title={`${point.count} appreciations`}
                      >
                        {/* Count label on hover */}
                        <div className="hidden group-hover:block absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs py-1 px-2 rounded whitespace-nowrap">
                          {point.count}
                        </div>
                      </div>
                    </div>
                    {/* Date label */}
                    <span className="text-xs text-gray-500 mt-2 text-center whitespace-nowrap transform -rotate-45 origin-top-left">
                      {new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No data available for this period</p>
        )}
      </div>

      {/* At-Risk Users Alert */}
      {atRiskUsers.length > 0 && (
        <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl shadow-md p-6 mb-8 card-hover border-l-4 border-red-500 fade-in">
          <div className="flex items-start mb-4">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-red-400 to-orange-500 flex items-center justify-center text-white shadow-md">
                <i className="fas fa-exclamation-triangle text-xl"></i>
              </div>
            </div>
            <div className="ml-4 flex-1">
              <h2 className="text-2xl font-bold text-gray-900">At-Risk Users</h2>
              <p className="text-sm text-gray-700 mt-1">
                {atRiskUsers.length} team member{atRiskUsers.length !== 1 ? 's' : ''} haven't received appreciation in 30+ days
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 max-h-64 overflow-y-auto">
            <div className="space-y-2">
              {atRiskUsers.slice(0, 10).map((user) => (
                <div key={user.id} className="flex justify-between items-center py-2 px-3 hover:bg-gray-50 rounded-md">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                      <span className="text-xs font-medium text-gray-700">{user.full_name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{user.full_name}</p>
                      {user.department && (
                        <p className="text-xs text-gray-500">{user.department}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-600">
                      {user.days_since_last_appreciation
                        ? `${user.days_since_last_appreciation} days ago`
                        : 'Never'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {atRiskUsers.length > 10 && (
              <p className="text-xs text-gray-500 text-center mt-3">
                +{atRiskUsers.length - 10} more users
              </p>
            )}
          </div>
        </div>
      )}

      {/* Department Engagement Comparison */}
      {departmentStats.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-6 mb-8 card-hover fade-in">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Department Engagement</h2>
          <p className="text-sm text-gray-600 mb-6">
            Participation rates across departments
          </p>
          <div className="space-y-4">
            {departmentStats.map((dept, index) => (
              <div key={index} className="border-l-4 border-indigo-500 bg-gray-50 p-4 rounded-r-lg">
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <span className="font-bold text-gray-900">{dept.department}</span>
                    <span className="text-xs text-gray-500 ml-2">
                      {dept.active_users}/{dept.total_users} active
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-indigo-600">{dept.participation_rate}%</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${dept.participation_rate}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  {dept.total_appreciations} total appreciations
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Company Values Breakdown */}
      {companyValuesStats.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-6 card-hover fade-in">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Company Values</h2>
          <p className="text-sm text-gray-600 mb-6">
            See which values are being recognized most
          </p>

          <div className="space-y-4">
            {companyValuesStats.slice(0, 6).map((value, index) => {
              const total = companyValuesStats.reduce((sum, v) => sum + v.count, 0);
              const percentage = total > 0 ? (value.count / total) * 100 : 0;

              return (
                <div key={value.id}>
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      <span className="font-medium text-gray-900">{value.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-blue-600">{value.count}</span>
                      <span className="text-xs text-gray-500 ml-2">{percentage.toFixed(0)}%</span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-purple-500 to-indigo-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
